import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, ArrowUpRight, MapPin, Calendar, CheckCircle2, Leaf, ShieldCheck, FileDown } from "lucide-react";
import { useLang } from "@/contexts/LangContext";
import PdfThumbnail from "@/components/PdfThumbnail";
import FavoriteButton from "@/components/FavoriteButton";
import { localized } from "@/lib/projectI18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ProjektDetailPage() {
  const { t, lang } = useLang();
  const { slug } = useParams();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [activeImg, setActiveImg] = useState(0);
  const [error, setError] = useState(false);

  useEffect(() => {
    axios
      .get(`${API}/projects/${slug}`)
      .then((res) => {
        setProject(res.data);
        setActiveImg(0);
      })
      .catch((err) => {
        console.warn("Project detail load failed:", err?.message);
        setError(true);
      });
    // API/axios are stable module-level references; refetch only when slug changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  if (error) {
    return (
      <div className="pt-40 pb-24 max-w-screen-2xl mx-auto px-6 text-center">
        <p className="text-white/60 mb-6">{t("project.not_found")}</p>
        <Link
          to="/projekte"
          className="rh-link text-sm tracking-[0.2em] uppercase"
          data-testid="back-to-projekte"
        >
          ← {t("common.back_to_overview")}
        </Link>
      </div>
    );
  }

  if (!project) {
    return <div className="pt-40 pb-24 text-center text-white/50">…</div>;
  }

  const allImages = [project.cover_image, ...(project.images || [])].filter(Boolean);

  // Extract YouTube video ID from common URL formats (watch?v=, youtu.be/, embed/)
  const videoId = (() => {
    if (!project.video_url) return null;
    const m = project.video_url.match(/(?:v=|youtu\.be\/|embed\/)([A-Za-z0-9_-]{11})/);
    return m ? m[1] : null;
  })();

  return (
    <div data-testid="projekt-detail-page" className="pt-32">
      {/* Hero */}
      <section className="max-w-screen-2xl mx-auto px-6 md:px-12 lg:px-16 pb-12">
        <button
          onClick={() => navigate("/projekte")}
          className="rh-link text-xs tracking-[0.2em] uppercase mb-10 inline-flex items-center gap-2"
          data-testid="projekt-back-btn"
        >
          <ArrowLeft size={14} strokeWidth={1.5} /> {t("common.back_to_overview")}
        </button>

        <div className="grid md:grid-cols-12 gap-8 items-end">
          <div className="md:col-span-8">
            <div className="flex items-center gap-4 text-[11px] tracking-[0.25em] uppercase text-white/60 mb-6">
              <span className="border border-white/20 bg-white/5 backdrop-blur-md px-3 py-1.5">{project.status}</span>
              <span
                data-testid="projekt-detail-eco-badge"
                className="inline-flex items-center gap-1.5 border border-emerald-300/40 bg-emerald-400/15 text-emerald-200 px-3 py-1.5"
              >
                <Leaf size={12} strokeWidth={1.5} /> {t("projects.card.energy_class")}
              </span>
              <span
                data-testid="projekt-detail-certified-badge"
                className="inline-flex items-center gap-1.5 border border-emerald-300/30 text-emerald-200/80 px-3 py-1.5"
              >
                <ShieldCheck size={12} strokeWidth={1.5} /> {t("projects.card.certified")}
              </span>
              <span className="inline-flex items-center gap-1.5">
                <MapPin size={12} strokeWidth={1.5} /> {project.location}
              </span>
              <span className="inline-flex items-center gap-1.5">
                <Calendar size={12} strokeWidth={1.5} /> {project.year}
              </span>
            </div>
            <h1 className="font-display text-4xl md:text-6xl lg:text-7xl font-light uppercase tracking-tighter leading-[0.95] flex items-start gap-4">
              <span>{project.title}</span>
              <FavoriteButton slug={project.slug} className="mt-2 shrink-0" />
            </h1>
          </div>
          <div className="md:col-span-4">
            <p className="text-base md:text-lg text-white/70 font-light leading-relaxed">
              {localized(project, "short_description", lang)}
            </p>
          </div>
        </div>
      </section>

      {/* Image gallery */}
      <section className="mb-16 md:mb-24">
        <div className="max-w-screen-2xl mx-auto px-6 md:px-12 lg:px-16">
          <div className="aspect-[16/9] overflow-hidden border border-white/10 mb-4">
            <img
              src={allImages[activeImg]}
              alt={project.title}
              className="w-full h-full object-cover"
              data-testid="projekt-main-image"
            />
          </div>
          {allImages.length > 1 && (
            <div className="grid grid-cols-3 md:grid-cols-6 gap-3">
              {allImages.map((img, i) => (
                <button
                  key={i}
                  onClick={() => setActiveImg(i)}
                  data-testid={`projekt-thumb-${i}`}
                  className={`aspect-[4/3] overflow-hidden border transition-all ${
                    activeImg === i
                      ? "border-white"
                      : "border-white/10 opacity-60 hover:opacity-100"
                  }`}
                >
                  <img src={img} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Video — YouTube embed (cinematic, premium) */}
      {videoId && (
        <section
          className="max-w-screen-2xl mx-auto px-6 md:px-12 lg:px-16 py-20 border-t border-white/10"
          data-testid="project-video-section"
        >
          <div className="grid md:grid-cols-12 gap-10 lg:gap-16 mb-12">
            <div className="md:col-span-5">
              <p className="text-[11px] tracking-[0.3em] uppercase text-white/55 mb-5">
                {t("project.video_eyebrow")}
              </p>
              <h2 className="font-display text-3xl md:text-5xl font-light uppercase tracking-tight leading-[1.05] break-words hyphens-auto">
                {t("project.video_title")}
              </h2>
            </div>
            <div className="md:col-span-7 md:pt-6 text-white/75 leading-[1.7] font-light md:text-lg">
              <p>{t("project.video_lede")}</p>
            </div>
          </div>

          <div className="relative w-full aspect-video bg-[#020c1f] overflow-hidden border border-white/10">
            <iframe
              src={`https://www.youtube-nocookie.com/embed/${videoId}?rel=0&modestbranding=1&color=white&playsinline=1`}
              title={project.title}
              className="absolute inset-0 w-full h-full"
              loading="lazy"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              allowFullScreen
              data-testid="project-video-iframe"
            />
          </div>
        </section>
      )}

      {/* Content */}
      <section className="max-w-screen-2xl mx-auto px-6 md:px-12 lg:px-16 py-16 grid md:grid-cols-12 gap-10 lg:gap-16 border-t border-white/10">
        <div className="md:col-span-7">
          <p className="text-xs tracking-[0.3em] uppercase text-white/50 mb-6">{t("project.about_eyebrow")}</p>
          <h2 className="font-display text-3xl md:text-4xl font-light uppercase tracking-tight leading-[1.1] mb-6">
            {t("project.about_title")}
          </h2>
          <p className="text-white/70 leading-relaxed text-base md:text-lg font-light">
            {localized(project, "description", lang)}
          </p>

          <div className="mt-12">
            <p className="text-xs tracking-[0.3em] uppercase text-white/50 mb-6">{t("project.highlights")}</p>
            <ul className="space-y-3">
              {project.highlights?.map((h) => (
                <li key={h} className="flex items-start gap-3 text-white/75">
                  <CheckCircle2 size={18} strokeWidth={1.25} className="mt-0.5 shrink-0 text-white/60" />
                  <span>{h}</span>
                </li>
              ))}
            </ul>
          </div>

          {project.floorplans?.length > 0 && (
            <div className="mt-12" data-testid="projekt-floorplans-section">
              <p className="text-xs tracking-[0.3em] uppercase text-white/50 mb-6">{t("project.floorplans")}</p>
              <ul className="space-y-3">
                {project.floorplans.map((fp) => (
                  <li key={fp.url}>
                    <a
                      href={fp.url}
                      target="_blank"
                      rel="noreferrer noopener"
                      download
                      data-testid={`projekt-floorplan-link-${fp.label}`}
                      className="flex items-center gap-4 text-white/75 hover:text-white border border-white/10 hover:border-white/30 p-3 transition-colors"
                    >
                      <PdfThumbnail url={fp.url} />
                      <span className="flex-1">{fp.label}</span>
                      <span className="text-[11px] tracking-[0.15em] uppercase text-white/40">PDF</span>
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {project.energy_certificate && (
            <div className="mt-12" data-testid="projekt-energy-certificate-section">
              <p className="text-xs tracking-[0.3em] uppercase text-white/50 mb-6">{t("project.energy_certificate")}</p>
              <a
                href={project.energy_certificate}
                target="_blank"
                rel="noreferrer noopener"
                download
                data-testid="projekt-energy-certificate-link"
                className="inline-flex items-center gap-4 text-emerald-200 hover:text-emerald-100 border border-emerald-300/30 hover:border-emerald-300/60 bg-emerald-400/10 px-4 py-3 transition-colors"
              >
                <FileDown size={18} strokeWidth={1.5} className="shrink-0" />
                <span className="flex-1">{t("project.energy_certificate_download")}</span>
                <span className="text-[11px] tracking-[0.15em] uppercase text-emerald-200/60">PDF</span>
              </a>
            </div>
          )}
        </div>

        <div className="md:col-span-5">
          <div className="border border-white/10 p-8 sticky top-28">
            <p className="text-xs tracking-[0.3em] uppercase text-white/50 mb-6">{t("project.specs")}</p>
            <dl className="space-y-5">
              {Object.entries(project.specs || {}).map(([k, v]) => (
                <div key={k} className="flex items-start justify-between gap-6 border-b border-white/10 pb-4">
                  <dt className="text-sm text-white/60">{k}</dt>
                  <dd className="text-sm text-white font-medium text-right">{v}</dd>
                </div>
              ))}
            </dl>
            <Link
              to="/kontakt"
              className="mt-8 w-full inline-flex items-center justify-center gap-3 bg-white text-[#051630] font-bold tracking-[0.18em] uppercase text-xs px-6 py-4 border border-white hover:bg-transparent hover:text-white transition-colors"
              data-testid="projekt-cta-kontakt"
            >
              <span>{t("project.cta_inquire")}</span>
              <ArrowUpRight size={16} strokeWidth={1.5} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
